import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useOptionalActiveSession } from '../api/active/sessionProvider';
import { createManagementApi, type ManagementApi } from './managementApi';
import {
  createPermissionAdminApi,
  type PermissionAdminApi,
} from './permissionAdminApi';

export interface PermissionPageRuntime {
  api: PermissionAdminApi;
  managementApi?: ManagementApi;
  currentUser: {
    id: string;
    displayName: string;
    companyId?: string;
    permissions: readonly string[];
  };
  canViewUsers: boolean;
  canViewRoles: boolean;
  canManageUsers: boolean;
  canManageDepartments?: boolean;
  canSyncWeCom?: boolean;
  canManageRoles: boolean;
  recoverUnauthorized(): Promise<void>;
}

const PermissionPageRuntimeContext =
  createContext<PermissionPageRuntime | null>(null);

export function PermissionPageRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: PermissionPageRuntime;
}) {
  return (
    <PermissionPageRuntimeContext.Provider value={value}>
      {children}
    </PermissionPageRuntimeContext.Provider>
  );
}

/**
 * The permission page is only meaningful with a real server session: the
 * department matrix and the user list both come from the frozen formal API, and
 * the server remains the only authorization decision point. The disposable
 * acceptance runtime keeps its own preview page instead.
 */
export function ActivePermissionPageRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  // The disposable acceptance runtime has no formal session at all. Rendering
  // the children without a runtime keeps that page tree alive and lets the
  // permission page fall back to its explicitly labelled preview.
  const session = useOptionalActiveSession();
  const readyState = session?.state.status === 'ready' ? session.state : null;
  const transport = session?.transport;
  const refresh = session?.refresh;
  const value = useMemo<PermissionPageRuntime | null>(() => {
    if (!readyState || !transport || !refresh) return null;
    return {
      api: createPermissionAdminApi(transport, readyState.csrfToken ?? null),
      managementApi: createManagementApi(
        transport,
        readyState.csrfToken ?? null,
        refresh,
        JSON.stringify([
          readyState.user.companyId,
          readyState.user.id,
          readyState.user.roleView?.roleId ?? null,
        ]),
      ),
      currentUser: {
        id: readyState.user.id,
        displayName: readyState.user.displayName,
        companyId: readyState.user.companyId,
        permissions: readyState.user.permissions,
      },
      canViewUsers: readyState.user.permissions.includes('identity.user.read'),
      canViewRoles: readyState.user.permissions.includes('identity.role.read'),
      canManageDepartments: readyState.user.permissions.includes(
        'identity.department.manage',
      ),
      canSyncWeCom: readyState.user.permissions.includes('identity.wecom.sync'),
      canManageUsers: [
        'identity.user.manage',
        'identity.user.profile.manage',
        'identity.user.status.manage',
        'identity.assignment.manage',
        'identity.session.revoke',
      ].some((permission) => readyState.user.permissions.includes(permission)),
      canManageRoles: readyState.user.permissions.includes(
        'identity.role.manage',
      ),
      recoverUnauthorized: refresh,
    };
  }, [readyState, refresh, transport]);
  if (value === null) return <>{children}</>;
  return (
    <PermissionPageRuntimeContext.Provider value={value}>
      {children}
    </PermissionPageRuntimeContext.Provider>
  );
}

export function usePermissionPageRuntime(): PermissionPageRuntime {
  const value = useContext(PermissionPageRuntimeContext);
  if (!value) throw new Error('PermissionPageRuntimeProvider is required');
  return value;
}

export function useOptionalPermissionPageRuntime(): PermissionPageRuntime | null {
  return useContext(PermissionPageRuntimeContext);
}
